import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import type { MoneyScaled } from '@quickbit/qb-design-system';

export enum TransactionType {
  BUY = 'BUY',
  SELL = 'SELL',
  SEND_INTERNAL = 'SEND_INTERNAL',
  SEND_EXTERNAL = 'SEND_EXTERNAL',
  RECEIVE_INTERNAL = 'RECEIVE_INTERNAL',
  RECEIVE_EXTERNAL = 'RECEIVE_EXTERNAL',
  SWAP_SOURCE = 'SWAP_SOURCE',
  SWAP_DESTINATION = 'SWAP_DESTINATION',
  REFUND = 'REFUND',
  SEND_FIAT_INTERNAL = 'SEND_FIAT_INTERNAL',
  SEND_FIAT_EXTERNAL = 'SEND_FIAT_EXTERNAL',
  RECEIVE_FIAT_INTERNAL = 'RECEIVE_FIAT_INTERNAL',
  RECEIVE_FIAT_EXTERNAL = 'RECEIVE_FIAT_EXTERNAL',
  CARD_PAYMENT = 'CARD_PAYMENT',
  INTERNAL_TRANSFER_SOURCE = 'INTERNAL_TRANSFER_SOURCE',
  INTERNAL_TRANSFER_DESTINATION = 'INTERNAL_TRANSFER_DESTINATION',
  YIELD_PAYOUT = 'YIELD_PAYOUT',
  GIVEAWAY = 'GIVEAWAY',
  OFFBOARDING_PAYOUT = 'OFFBOARDING_PAYOUT',
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  INTERGIRO_SCA_REQUIRED = 'INTERGIRO_SCA_REQUIRED',
  PENDING_TRAVEL_RULE = 'PENDING_TRAVEL_RULE',
  MANUAL_REVIEW = 'MANUAL_REVIEW',
}

export interface TransactionMinimal {
  id: string;
  type: TransactionType;
  supportId: string;
  walletId: string;
  amount: MoneyScaled;
  amountReference: MoneyScaled;
  createdAt: Date;
  updatedAt: Date;
  status: string;
  exchangeRatesId: number;
  euroAmount: MoneyScaled;
  serviceFee: MoneyScaled;
  referenceFee: MoneyScaled;
  totalReference: MoneyScaled;
  nexusTransactionCode?: string;
  balanceAtTransactionTime?: MoneyScaled;
  balanceReferenceAtTransactionTime?: MoneyScaled;
  balanceAfterTransaction?: MoneyScaled;
  balanceReferenceAfterTransaction?: MoneyScaled;
  blockchainTransactionId?: string;
}

export interface TransactionBase extends TransactionMinimal {
  type: TransactionType;
  exchangeRateReference: MoneyScaled;
  userId: string;
  isFrozen: boolean;
  frozenReason?: string;
}

type CardBrand = 'VISA' | 'AMEX' | 'MASTERCARD' | 'UNKNOWN';

export interface MaskedCreditCard {
  type: 'CREDIT_CARD' | 'GOOGLE_PAY' | 'APPLE_PAY';
  cardNumberLastDigits?: string;
  cardBrand: CardBrand;
}

export interface BuyTransaction extends TransactionBase {
  type: TransactionType.BUY;
  paymentMethod: MaskedCreditCard;
  frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  intergiroTransactionCode?: string;
  pspFee?: MoneyScaled;
}

export interface SellTransaction extends TransactionBase {
  type: TransactionType.SELL;
}

interface UserDestination {
  profileImage?: string;
  firstName?: string;
  lastName?: string;
}

interface MerchantDestination {
  uuid: string;
  name: string;
  userId: string;
  redirectUrl?: string;
  orderId?: string;
}

interface ContactDestination {
  uuid: string;
  label: string;
  isInternal: boolean;
}

export interface Destination {
  address: string;
  type: 'PERSON' | 'MERCHANT';
  user?: UserDestination;
  merchant?: MerchantDestination;
  contact?: ContactDestination;
}

interface SendTransaction {
  destination?: Destination;
}

export interface SendInternalTransaction
  extends TransactionBase,
    SendTransaction {
  type: TransactionType.SEND_INTERNAL;
}

export interface SendExternalTransaction
  extends TransactionBase,
    SendTransaction {
  type: TransactionType.SEND_EXTERNAL;
  blockchainTransactionId?: string;
  blockchainExplorerUrl?: string;
  networkFee: MoneyScaled;
  total: MoneyScaled;
}

export interface ReceiveInternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_INTERNAL;
  origin?: Destination;
}

export interface ReceiveExternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_EXTERNAL;
  blockchainTransactionId?: string;
  blockchainExplorerUrl?: string;
  networkFee: MoneyScaled;
  total: MoneyScaled;
}

export interface SwapSourceTransaction extends TransactionBase {
  type: TransactionType.SWAP_SOURCE;
  swapId: string;
  destinationWalletId: string;
  destinationAmount: MoneyScaled;
  destinationToSourceExchangeRate: MoneyScaled;
}

export interface SwapDestinationTransaction extends TransactionBase {
  type: TransactionType.SWAP_DESTINATION;
  swapId: string;
  sourceWalletId: string;
  sourceAmount: MoneyScaled;
  sourceToDestinationExchangeRate: MoneyScaled;
}

export interface RefundTransaction extends TransactionBase {
  type: TransactionType.REFUND;
}

interface BankAccountAddress {
  name?: string;
  iban: string;
  bic: string;
}

export interface SendFiatInternalTransaction extends TransactionBase {
  type: TransactionType.SEND_FIAT_INTERNAL;
  destination: BankAccountAddress;
  destinationUser: UserDestination;
}

export interface SendFiatExternalTransaction extends TransactionBase {
  type: TransactionType.SEND_FIAT_EXTERNAL;
  destination: BankAccountAddress;
}

export interface ReceiveFiatInternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_FIAT_INTERNAL;
  source: BankAccountAddress;
  sourceUser: UserDestination;
}

export interface ReceiveFiatExternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_FIAT_EXTERNAL;
  sourceIban: string;
}

interface CardPaymentMerchant {
  name: string;
  location: string;
}

export interface CardPaymentTransaction extends TransactionBase {
  type: TransactionType.CARD_PAYMENT;
  amountPayment: MoneyScaled;
  exchangeRatePayment: MoneyScaled;
  cardId: string;
  merchant?: CardPaymentMerchant;
}

export interface InternalTransferSourceTransaction extends TransactionBase {
  type: TransactionType.INTERNAL_TRANSFER_SOURCE;
  destinationWalletId: string;
  internalTransferType: string;
}

export interface InternalTransferDestinationTransaction
  extends TransactionBase {
  type: TransactionType.INTERNAL_TRANSFER_DESTINATION;
  sourceWalletId: string;
  internalTransferType: string;
}

export interface YieldPayoutTransaction extends TransactionBase {
  type: TransactionType.YIELD_PAYOUT;
}

export interface GiveawayTransaction extends TransactionBase {
  type: TransactionType.GIVEAWAY;
}

export interface OffboardingPayoutTransaction extends TransactionBase {
  type: TransactionType.OFFBOARDING_PAYOUT;
}

export type Transaction =
  | BuyTransaction
  | SellTransaction
  | SendInternalTransaction
  | SendExternalTransaction
  | ReceiveInternalTransaction
  | ReceiveExternalTransaction
  | SwapSourceTransaction
  | SwapDestinationTransaction
  | RefundTransaction
  | SendFiatInternalTransaction
  | SendFiatExternalTransaction
  | ReceiveFiatInternalTransaction
  | ReceiveFiatExternalTransaction
  | CardPaymentTransaction
  | InternalTransferSourceTransaction
  | InternalTransferDestinationTransaction
  | YieldPayoutTransaction
  | GiveawayTransaction
  | OffboardingPayoutTransaction;

export const reuploadTransactionToCM1 = async (
  transactionId: string
): Promise<void> => {
  return fetchAPI(
    `v1/app-user/transactions/${transactionId}/retry-cm1-upload`,
    {
      method: 'POST',
    }
  );
};

export const refundTransaction = async (transactionId: string) => {
  const resp = await fetchAPI<{ success: boolean }>(
    `v1/app-user/transactions/${transactionId}/refund`,
    {
      method: 'POST',
    }
  );
  return resp.success;
};

export interface SearchTransactionsFilters {
  uuid?: string;
  userId?: string;
  walletId?: string;
  transactionType?: string;
  createdFrom?: string;
  createdTo?: string;
  status?: string;
  currency?: string;
  nexusTransactionCode?: string;
  travelRuleTransactionId?: string;
}

export interface SearchTransactionsResponse {
  transactions: TransactionMinimal[];
  aggregatedData: { count: number };
}

export const searchTransactions = async ({
  filters,
  pagination,
}: {
  filters: SearchTransactionsFilters;
  pagination?: {
    page: number;
    size: number;
  };
}) => {
  const response = await fetchAPI<SearchTransactionsResponse>(
    `v1/transactions:search`,
    {
      method: 'POST',
      body: {
        filters,
        pagination,
      },
    }
  );

  return {
    ...response,
    transactions: response.transactions.map((transaction) => ({
      ...transaction,
      createdAt: new Date(transaction.createdAt),
      updatedAt: new Date(transaction.updatedAt),
    })),
  };
};

export const exportTransactions = async (
  filters: SearchTransactionsFilters
) => {
  const response = await fetchAPI(`v1/transactions:export`, {
    method: 'POST',
    body: { filters },
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  return new Blob(safeChunks, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const exportTransactionsCsv = async (
  filters: SearchTransactionsFilters
) => {
  const response = await fetchAPI(`v1/transactions:export-csv`, {
    method: 'POST',
    body: { filters },
    stream: true,
  });
  const safeChunks = response.map((c) => new Uint8Array(c));
  return new Blob(safeChunks, { type: 'text/csv' });
};
export const getTransactionDetails = async (transactionId: string) => {
  return await fetchAPI<Transaction>(`v1/transactions/${transactionId}`);
};

export const getUserBalance = async (userId: string) => {
  return await fetchAPI<{
    balances: Record<string, Record<string, MoneyScaled>>;
  }>(`v1/users/${userId}/balances`);
};

export const getWalletAddresses = async (
  userId: string,
  currencies: string[]
) => {
  return await fetchAPI<Record<string, string>>(
    `v1/users/${userId}/addresses?currencies=${currencies.join(',')}`
  );
};

interface CreateCryptoRefundPreviewResponse {
  previewId: string;
  isInternal: boolean;
}

export const createCryptoRefundPreview = async (
  transactionId: string,
  destinationAddress: string
) => {
  return await fetchAPI<CreateCryptoRefundPreviewResponse>(
    `v1/app-user/transactions/${transactionId}/crypto-refund/preview`,
    {
      method: 'POST',
      body: {
        destinationAddress,
      },
    }
  );
};

interface CompleteRefundResponse {
  transactionId: string;
  status: string;
}

export const completeCryptoRefund = async (previewId: string) => {
  return fetchAPI<CompleteRefundResponse>(
    `v1/app-user/crypto-refund/${previewId}/complete`,
    {
      method: 'POST',
    }
  );
};

export interface TravelRuleInfoStatus {
  status: boolean;
}

export const getTravelRuleInfoStatus = async (previewId: string) => {
  return fetchAPI<TravelRuleInfoStatus>(
    `v1/app-user/crypto-refund/${previewId}/status`
  );
};
